<script setup>
import { computed, h, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { formatTimeTranslated } from 'dashboard/composables/useReportMetrics';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/vue-table';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import CsatEmptyState from './components/CsatEmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const columnHelper = createColumnHelper();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const hasFetched = ref(false);

const reports = useMapGetter('summaryReports/getContactSummaryReports');
const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(
  () => !hasFetched.value || uiFlags.value.isFetchingContactSummaryReports
);

const MEDALS = ['🥇', '🥈', '🥉'];

const contactDisplayName = contact =>
  contact.name || contact.email || contact.phoneNumber || `#${contact.id}`;

const renderCount = value =>
  value == null ? '--' : Number(value).toLocaleString();

const renderAvgTime = value => (value ? formatTimeTranslated(value) : '--');

const topThree = computed(() => (reports.value || []).slice(0, 3));
const podium = computed(() => {
  const [first, second, third] = topThree.value;
  return [
    second && { contact: second, medal: MEDALS[1], position: 2 },
    first && { contact: first, medal: MEDALS[0], position: 1 },
    third && { contact: third, medal: MEDALS[2], position: 3 },
  ].filter(Boolean);
});

const tableData = computed(() =>
  (reports.value || []).map((row, index) => ({
    ...row,
    rank: index + 1,
    displayName: contactDisplayName(row),
  }))
);

const contactCell = cellProps =>
  h(
    'router-link',
    {
      to: {
        name: 'contact_profile_dashboard',
        params: { contactId: cellProps.row.original.id },
      },
      class: 'flex items-center gap-2 min-w-0 text-n-slate-12 hover:underline',
    },
    [
      h(Thumbnail, {
        username: cellProps.row.original.displayName,
        size: '24px',
      }),
      h('span', { class: 'flex flex-col min-w-0' }, [
        h(
          'span',
          { class: 'truncate font-medium' },
          cellProps.row.original.displayName
        ),
        cellProps.row.original.email
          ? h(
              'span',
              { class: 'truncate text-xs text-n-slate-11' },
              cellProps.row.original.email
            )
          : null,
      ]),
    ]
  );

const columns = computed(() => [
  columnHelper.accessor('rank', {
    header: t('CONTACT_REPORTS.TABLE.RANK'),
    size: 64,
    enableSorting: false,
    cell: cellProps =>
      h('span', { class: 'text-n-slate-11 font-medium' }, cellProps.getValue()),
  }),
  columnHelper.accessor('displayName', {
    header: t('CONTACT_REPORTS.TABLE.CONTACT'),
    size: 280,
    cell: contactCell,
  }),
  columnHelper.accessor('conversationsCount', {
    header: t('CONTACT_REPORTS.TABLE.CONVERSATIONS'),
    size: 140,
    cell: cellProps => renderCount(cellProps.getValue()),
  }),
  columnHelper.accessor('messagesCount', {
    header: t('CONTACT_REPORTS.TABLE.MESSAGES'),
    size: 140,
    cell: cellProps => renderCount(cellProps.getValue()),
  }),
  columnHelper.accessor('totalResolutionTime', {
    header: t('CONTACT_REPORTS.TABLE.TOTAL_HANDLING_TIME'),
    size: 200,
    cell: cellProps => renderAvgTime(cellProps.getValue()),
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: t('CONTACT_REPORTS.TABLE.AVG_HANDLING_TIME'),
    size: 200,
    cell: cellProps => renderAvgTime(cellProps.getValue()),
  }),
]);

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: true,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});

const fetchReports = async ({ inboxId, teamId, agentIds } = {}) => {
  await store.dispatch('summaryReports/fetchContactSummaryReports', {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
    inboxId,
    teamId,
    agentIds,
  });
  hasFetched.value = true;
};

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchReports({
    inboxId: updatedFilter.selectedInbox?.id,
    teamId: updatedFilter.selectedTeam?.id,
    agentIds: updatedFilter.selectedAgents?.length
      ? updatedFilter.selectedAgents.map(agent => agent.id)
      : undefined,
  });
};
</script>

<template>
  <ReportHeader
    :header-title="$t('CONTACT_REPORTS.HEADER')"
    :header-description="$t('CONTACT_REPORTS.DESCRIPTION')"
  />

  <ReportFilterSelector
    show-agents-filter
    show-inbox-filter
    show-team-filter
    @filter-change="onFilterChange"
  />

  <div
    v-if="isLoading"
    class="flex items-center justify-center gap-2 py-16 text-n-slate-11"
  >
    <Spinner />
    <span>{{ $t('CONTACT_REPORTS.LOADING') }}</span>
  </div>

  <template v-else-if="tableData.length">
    <div
      v-if="podium.length"
      class="mt-6 flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-6"
    >
      <div class="flex flex-col gap-1 text-center">
        <h3 class="mb-0 text-base font-medium text-n-slate-12">
          {{ $t('CONTACT_REPORTS.RANKING.TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('CONTACT_REPORTS.RANKING.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex items-end justify-center gap-4">
        <div
          v-for="entry in podium"
          :key="entry.contact.id"
          class="flex w-44 flex-col items-center gap-2 rounded-xl border bg-n-solid-1 p-4 text-center"
          :class="
            entry.position === 1
              ? 'border-n-brand shadow-md -translate-y-2'
              : 'border-n-weak'
          "
        >
          <span class="text-2xl leading-none">{{ entry.medal }}</span>
          <Thumbnail
            :username="contactDisplayName(entry.contact)"
            size="40px"
          />
          <router-link
            :to="{
              name: 'contact_profile_dashboard',
              params: { contactId: entry.contact.id },
            }"
            class="text-sm font-medium text-n-slate-12 hover:underline"
          >
            {{ contactDisplayName(entry.contact) }}
          </router-link>
          <span class="text-sm font-semibold text-n-blue-text">
            {{
              $t('CONTACT_REPORTS.RANKING.CONVERSATIONS', {
                count: entry.contact.conversationsCount,
              })
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              $t('CONTACT_REPORTS.RANKING.MESSAGES', {
                count: entry.contact.messagesCount,
              })
            }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="mt-5 flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <Table :table="table" />
    </div>
  </template>

  <CsatEmptyState
    v-else
    :title="$t('CONTACT_REPORTS.NO_RECORDS')"
    :description="$t('CONTACT_REPORTS.NO_RECORDS_DESCRIPTION')"
    icon="i-lucide-users"
  />
</template>
